import { useEffect, useMemo, useRef, useState } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import { ArrowLeft, Loader2, MessageCircle, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { getOperatingSystemDetailPath } from "@/lib/operating-systems";
import { useSupport } from "./SupportContext";

const formatMessageTimestamp = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const dateLabel = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
  const timeLabel = new Intl.DateTimeFormat([], {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);

  return `${dateLabel} ${timeLabel}`;
};

const createSupportClientMessageId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `support-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const SupportWidget = () => {
  const {
    enabled,
    loading,
    sending,
    isOpen,
    unreadCount,
    messages,
    openWidget,
    closeWidget,
    toggleWidget,
    sendMessage,
  } = useSupport();
  const { toast } = useToast();
  const location = useLocation();
  const [draft, setDraft] = useState("");
  const [pendingClientMessageId, setPendingClientMessageId] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  const messageCountLabel = useMemo(() => {
    if (loading) return "Loading support chat...";
    if (!messages.length) return "Start a conversation with Applyt support.";
    return `${messages.length} message${messages.length === 1 ? "" : "s"} in this thread`;
  }, [loading, messages.length]);

  useEffect(() => {
    if (!isOpen) return;
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [isOpen, messages]);

  const runMatch = matchPath("/applications/:osId/run", location.pathname)
    ?? matchPath("/operating-systems/:osId/run", location.pathname);
  const backToApplytPath = runMatch?.params.osId ? getOperatingSystemDetailPath(runMatch.params.osId) : null;

  if (!enabled && !backToApplytPath) {
    return null;
  }

  const handleSend = async () => {
    const nextDraft = draft.trim();
    if (!nextDraft) return;
    const clientMessageId = pendingClientMessageId ?? createSupportClientMessageId();

    try {
      setPendingClientMessageId(clientMessageId);
      await sendMessage({ text: nextDraft, clientMessageId });
      setDraft("");
      setPendingClientMessageId(null);
      openWidget();
    } catch (error) {
      const description = error instanceof Error ? error.message : "Failed to send support message";
      toast({
        title: "Support message failed",
        description,
        variant: "destructive",
      });
    }
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter") return;
    if (event.nativeEvent.isComposing) return;

    if (event.shiftKey) {
      event.preventDefault();
      const target = event.currentTarget;
      const start = target.selectionStart ?? draft.length;
      const end = target.selectionEnd ?? draft.length;
      const nextValue = `${draft.slice(0, start)}\n${draft.slice(end)}`;
      setDraft(nextValue);
      requestAnimationFrame(() => {
        target.selectionStart = target.selectionEnd = start + 1;
      });
      return;
    }

    event.preventDefault();
    await handleSend();
  };

  // Hover-to-open removed — widget opens only when the Support tab is clicked.

  return (
    <div className="pointer-events-none fixed inset-y-0 right-0 z-50 flex items-center">
      <div className="pointer-events-auto flex flex-row-reverse items-center">
        <div className="flex flex-col items-end gap-2">
          {backToApplytPath && (
            <Link
              to={backToApplytPath}
              className="group relative flex h-44 w-10 flex-col items-center justify-center gap-3 rounded-l-2xl border border-r-0 border-slate-800/80 bg-[#081329] text-white shadow-2xl transition-all duration-300 ease-out hover:-translate-x-1 hover:bg-[#0b1a36] hover:shadow-[0_8px_30px_-4px_hsl(var(--primary)/0.5)]"
              aria-label="Back to Applyt"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white shadow-[0_0_0_3px_hsl(var(--primary)/0.25)] transition-transform duration-300 ease-out group-hover:scale-110">
                <ArrowLeft className="h-4 w-4" />
              </div>
              <span
                className="text-[9px] font-semibold uppercase tracking-[0.08em] text-white/80 transition-colors duration-300 group-hover:text-white"
                style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
              >
                Back to Applyt
              </span>
            </Link>
          )}

          {enabled && (
            <button
              type="button"
              onClick={toggleWidget}
              className={cn(
                "group relative flex h-44 w-10 flex-col items-center justify-center gap-4 rounded-l-2xl border border-r-0 border-slate-800/80 bg-[#081329] text-white shadow-2xl transition-all duration-300 ease-out hover:-translate-x-1 hover:bg-[#0b1a36] hover:shadow-[0_8px_30px_-4px_hsl(var(--primary)/0.5)]",
                isOpen && "translate-x-0",
              )}
              aria-expanded={isOpen}
              aria-label={isOpen ? "Close support chat" : "Open support chat"}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white shadow-[0_0_0_3px_hsl(var(--primary)/0.25)] transition-transform duration-300 ease-out group-hover:scale-110 group-hover:animate-pulse">
                <MessageCircle className="h-4 w-4" />
              </div>
              <span
                className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80 transition-colors duration-300 group-hover:text-white"
                style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
              >
                Support
              </span>
              {unreadCount > 0 && (
                <span className="absolute -left-1 -top-1 inline-flex min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Slide-out panel */}
        {enabled && (
          <div
            className={cn(
              "overflow-hidden transition-[width,opacity] duration-300 ease-out",
              isOpen ? "w-[min(100vw-3.5rem,24rem)] opacity-100" : "w-0 opacity-0",
            )}
          >
            <Card
              className={cn(
                "flex h-[90vh] w-[min(100vw-3.5rem,24rem)] flex-col rounded-r-none border-slate-800/80 bg-[#081329]/95 text-white shadow-2xl backdrop-blur-sm",
              )}
            >
              <CardHeader className="space-y-3 border-b border-white/10 pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <CardTitle className="text-lg font-semibold">Applyt support</CardTitle>
                    <p className="text-sm text-white/70">Send a message and we’ll reply here.</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-full text-white/70 hover:bg-white/10 hover:text-white"
                    onClick={closeWidget}
                    aria-label="Close support chat"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-3 text-xs text-white/60">
                  <span>{messageCountLabel}</span>
                </div>
              </CardHeader>
              <CardContent className="flex min-h-0 flex-1 flex-col p-0">
                <ScrollArea className="min-h-0 flex-1 px-4 py-4">
                  <div className="space-y-3">
                    {!messages.length && (
                      <div className="rounded-2xl border border-dashed border-white/10 bg-white/5 px-4 py-5 text-sm text-white/65">
                        Ask about billing, access, bugs, or anything blocking you in Applyt.
                      </div>
                    )}

                    {messages.map((message) => {
                      const isCustomer = message.sender_type === "customer";
                      return (
                        <div
                          key={message.id}
                          className={cn("flex", isCustomer ? "justify-end" : "justify-start")}
                        >
                          <div
                            className={cn(
                              "max-w-[86%] rounded-2xl px-4 py-3 shadow-sm",
                              isCustomer
                                ? "rounded-br-md bg-primary text-white"
                                : "rounded-bl-md border border-white/10 bg-white/[0.08] text-white",
                            )}
                          >
                            <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
                              <span className={cn(
                                "uppercase tracking-[0.16em]",
                                isCustomer ? "text-white/75" : "text-white/55",
                              )}>
                                {isCustomer ? "You" : "Applyt support"}
                              </span>
                              <span className={cn(
                                "normal-case tracking-normal",
                                isCustomer ? "text-white/60" : "text-white/45",
                              )}>
                                {formatMessageTimestamp(message.created_at)}
                              </span>
                            </div>
                            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                              {message.content}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={endRef} />
                  </div>
                </ScrollArea>

                <div className="border-t border-white/10 px-4 py-4">
                  <div className="space-y-3">
                    <Textarea
                      value={draft}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        if (pendingClientMessageId && nextValue !== draft) {
                          setPendingClientMessageId(null);
                        }
                        setDraft(nextValue);
                      }}
                      onKeyDown={handleKeyDown}
                      placeholder="Write your message..."
                      className="min-h-[96px] resize-none border-white/10 bg-white/5 text-white placeholder:text-white/35 focus-visible:ring-primary"
                      disabled={sending}
                    />
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs text-white/45">
                        Press Enter to send, Shift+Enter for a new line.
                      </span>
                      <Button
                        onClick={handleSend}
                        disabled={sending || !draft.trim()}
                        className="rounded-full bg-primary px-4 text-white hover:bg-primary/90"
                      >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        Send
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
